import { ColumnConfig, ColumnConfigOptions, ColumnText, CombinedStringFormatter, ConditionalExtractor, DateFormatter, ExtractAndFormatExtractor, LodashExtractor, LodashSeriesEnricherMapper, NumberFormatter, StaticValueExtractor, StoreNestedFilterExtractor, TabConfig, TablePerPortfolioSeriesExtractorConfig, TextAppendFormatter } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';

import { FAMILY_PERFORMANCE_COLUMN_CONFIG, MODEL_PORTFOLIO_FILTER_BY_GROUP_ID, MODEL_PORTFOLIO_FILTER_FUNCTION } from './family-column.config';

export const PERFORMANCE = new TabConfig(
	'performance',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(
			['families', 'familyPerformance', 'familyOverview'],
			['families', 'familyPerformance', 'familyOverview'],
			'families.models',
			(portfolio: any, data: any) => {
				return {
					...portfolio,
					managementType: portfolio?.managementType || data?.families?.managementType,
					...(data?.familyPerformance?.models ?? []).find(model => model && (model.id === portfolio.id)),
					...(data?.familyOverview?.models ?? []).find(model => model && (model.id === portfolio.id))
				};
			},
			MODEL_PORTFOLIO_FILTER_BY_GROUP_ID
		),
		new ColumnText('MODEL', 'Model'),
		[
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'modelName',
				label: new ColumnText('MODEL', 'Model'),
				extractor: new ConditionalExtractor(
					model => model.managementType === 'research',
					new ExtractAndFormatExtractor('shortName', '', (description) => '*' + description),
					new LodashExtractor('shortName', '')
				),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			ColumnConfig.create({
				id: 'estimatedRisk',
				label: new ColumnText('ESTIMATED_RISK', 'Estimated Risk (%)', null, { sortable: false, align: 'text-align-left' }),
				extractor: new LodashExtractor('risk.total', ''),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true,
				infoBubble: new ColumnText('HOLDINGS_EST_RISK_TOOLTIP')

			} as ColumnConfigOptions<LodashExtractor>),
			new ColumnConfig(
				new ColumnText('PERFORMANCE_TYPE', 'Performance Type', null, { align: 'text-align-left' }),
				null,
				new StaticValueExtractor(null), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'performanceType',
				new CombinedStringFormatter('Gross', 'Gross of an advisory fee')
			),
			ColumnConfig.create({
				id: 'oneMonth',
				label: new ColumnText('LAST_MONTH', 'Last Month (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn1m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeMonth',
				label: new ColumnText('THREE_MONTH', '3 Month (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn3m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'yearToDate',
				label: new ColumnText('YEAR_TO_DATE', 'Year to Date (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			// TODO wdsdecom:  config to be updated
			ColumnConfig.create({
				id: 'oneYear',
				label: new ColumnText('ONE_YEAR', '1 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn1Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeYear',
				label: new ColumnText('THREE_YEAR', '3 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn3Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'fiveYear',
				label: new ColumnText('FIVE_YEAR', '5 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn5Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'tenYear',
				label: new ColumnText('TEN_YEAR', '10 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn10Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'inception',
				label: new ColumnText('INCEPTION', 'Since Inception (%)', null, { sortable: false }),
				extractor: new ConditionalExtractor(
					performance => performance?.performance?.exPostMetrics?.[0]?.months < 180,
					new ConditionalExtractor(
						performance => performance?.performance?.exPostMetrics?.[0]?.months <= 12,
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'totalReturn', desiredValueExpression: 'metricValue' }]
						),
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'annualizedReturnTotal', desiredValueExpression: 'metricValue' }]
						)),
					new LodashExtractor('', '-')
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			ColumnConfig.create({
				id: 'asOfDate',
				label: new ColumnText('AS_OF_DATE', 'As of'),
				extractor: new LodashExtractor('performance.exPostMetrics[0].interval.stopDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>)
		],
		FAMILY_PERFORMANCE_COLUMN_CONFIG,
		null
	)
);

export const PERFORMANCE_PDF = new TabConfig(
	'performance',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(
			['families', 'familyPerformance', 'familyOverview'],
			['families', 'familyPerformance', 'familyOverview'],
			'families.models',
			(portfolio: any, data: any) => {
				return {
					...portfolio,
					managementType: portfolio?.managementType || data?.families?.managementType,
					id: portfolio.portfolioId || portfolio.id,
					...data?.familyPerformance?.models?.find(model => model?.id === portfolio.id),
					...data?.familyOverview?.models?.find(model => model?.id === portfolio.id)
				};
			},
			MODEL_PORTFOLIO_FILTER_FUNCTION
		),
		new ColumnText('MODEL', 'Model'),
		[
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'modelName',
				label: new ColumnText('MODEL', 'Model'),
				extractor: new ConditionalExtractor(
					model => model.managementType === 'research',
					new ExtractAndFormatExtractor('shortName', '', (description) => '*' + description),
					new LodashExtractor('shortName', '')
				),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			new ColumnConfig(
				new ColumnText('PERFORMANCE_TYPE', 'Performance Type', null, { align: 'text-align-left' }),
				null,
				new StaticValueExtractor(null), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'performanceType',
				new CombinedStringFormatter('Gross', 'Gross of an advisory fee')
			),
			ColumnConfig.create({
				id: 'oneMonth',
				label: new ColumnText('LAST_MONTH', 'Last Month (%)', null, { sortable: false }),
				// extractor: new LodashExtractor('oneMonth', ''),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn1m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeMonth',
				label: new ColumnText('THREE_MONTH', '3 Month (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn3m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'yearToDate',
				label: new ColumnText('YEAR_TO_DATE', 'Year to Date (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'oneYear',
				label: new ColumnText('ONE_YEAR', '1 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn1Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeYear',
				label: new ColumnText('THREE_YEAR', '3 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn3Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'fiveYear',
				label: new ColumnText('FIVE_YEAR', '5 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn5Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'tenYear',
				label: new ColumnText('TEN_YEAR', '10 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn10Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'inception',
				label: new ColumnText('INCEPTION', 'Since Inception (%)', null, { sortable: false }),
				extractor: new ConditionalExtractor(
					performance => performance?.performance?.exPostMetrics?.[0]?.months < 180,
					new ConditionalExtractor(
						performance => performance?.performance?.exPostMetrics?.[0]?.months <= 12,
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'totalReturn', desiredValueExpression: 'metricValue' }]
						),
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'annualizedReturnTotal', desiredValueExpression: 'metricValue' }]
						)),
					new LodashExtractor('', '-')
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			ColumnConfig.create({
				id: 'asOfDate',
				label: new ColumnText('AS_OF_DATE', 'As of'),
				extractor: new LodashExtractor('performance.exPostMetrics[0].interval.stopDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>)
		],
		FAMILY_PERFORMANCE_COLUMN_CONFIG
	)
);

export const PERFORMANCE_BENCHMARK = new TabConfig(
	'performance.benchmark',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(
			['families', 'familyPerformance'],
			['families', 'familyPerformance'], 'families.models',
			(portfolio: any, data: any) => {
				return {
					...portfolio,
					...data?.familyPerformance?.modelsBenchmark?.find(model => model?.id === portfolio.id) ?? []
				};
			},
			MODEL_PORTFOLIO_FILTER_BY_GROUP_ID
		),
		null,
		[
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'modelName',
				extractor: new LodashExtractor('shortName', ''),
				labelFormatter: new TextAppendFormatter(' Benchmark'),
				visible: true
			} as ColumnConfigOptions<LodashExtractor>),
			new ColumnConfig(
				new ColumnText('ESTIMATED_RISK', 'Estimated Risk (%)', null, { sortable: false }),
				null,
				new StaticValueExtractor(' '), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'estimatedRisk'
			),
			new ColumnConfig(
				new ColumnText('PERFORMANCE_TYPE', 'Performance Type', null, { align: 'text-align-left' }),
				null,
				new StaticValueExtractor(null), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'performanceType',
				new CombinedStringFormatter('Gross', 'Gross of an advisory fee')
			),
			ColumnConfig.create({
				id: 'oneMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn1m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn3m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'yearToDate',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'oneYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn1Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn3Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'fiveYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn5Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'tenYear',
				label: new ColumnText('TEN_YEAR_BENCHMARK', '10 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn10Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'inception',
				extractor: new ConditionalExtractor(
					performance => performance?.benchmarkAnalysis?.performance?.exPostMetrics?.[0]?.months < 180,
					new ConditionalExtractor(
						performance => performance?.benchmarkAnalysis?.performance?.exPostMetrics?.[0]?.months <= 12,
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'totalReturn', desiredValueExpression: 'metricValue' }]
						),
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'annualizedReturnTotal', desiredValueExpression: 'metricValue' }]
						)),
					new LodashExtractor('', '-')
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>)
		],
		FAMILY_PERFORMANCE_COLUMN_CONFIG
	)
);

export const PERFORMANCE_BENCHMARK_PDF = new TabConfig(
	'performance.benchmark',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(
			['families', 'familyPerformance'],
			['families', 'familyPerformance'],
			'families.models',
			(portfolio: any, data: any) => {
				return {
					...portfolio,
					...data?.familyPerformance?.modelsBenchmark?.find(model => model?.id === portfolio.id || model.id === portfolio.portfolioId) ?? []
				};
			},
			MODEL_PORTFOLIO_FILTER_FUNCTION
		),
		null,
		[
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'modelName',
				extractor: new LodashExtractor('shortName', ''),
				labelFormatter: new TextAppendFormatter(' Benchmark'),
				visible: true
			} as ColumnConfigOptions<LodashExtractor>),
			new ColumnConfig(
				new ColumnText('PERFORMANCE_TYPE', 'Performance Type', null, { align: 'text-align-left' }),
				null,
				new StaticValueExtractor(null), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'performanceType',
				new CombinedStringFormatter('Gross', 'Gross of an advisory fee')
			),
			ColumnConfig.create({
				id: 'oneMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn1m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn3m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'yearToDate',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'oneYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn1Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn3Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'fiveYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn5Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'tenYear',
				label: new ColumnText('TEN_YEAR_BENCHMARK', '10 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn10Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'inception',
				extractor: new ConditionalExtractor(
					performance => performance?.benchmarkAnalysis?.performance?.exPostMetrics?.[0]?.months < 180,
					new ConditionalExtractor(
						performance => performance?.benchmarkAnalysis?.performance?.exPostMetrics?.[0]?.months <= 12,
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'totalReturn', desiredValueExpression: 'metricValue' }]
						),
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'annualizedReturnTotal', desiredValueExpression: 'metricValue' }]
						)),
					new LodashExtractor('', '-')
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>)
		],
		FAMILY_PERFORMANCE_COLUMN_CONFIG
	)
);

export const PERFORMANCE_CUSTOM_NET = new TabConfig(
	'performance.custom.net',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(
			['families', 'familyPerformance'],
			['families', 'familyPerformance'],
			'families.models',
			(portfolio: any, data: any) => {
				return {
					...portfolio,
					...data?.familyPerformance?.models?.find(model => model?.id === portfolio.id) ?? []
				};
			},
			MODEL_PORTFOLIO_FILTER_BY_GROUP_ID
		),
		null,
		[
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'modelName',
				label: new ColumnText('MODEL', 'Model'),
				extractor: new ConditionalExtractor(
					model => model.managementType === 'research',
					new ExtractAndFormatExtractor('shortName', '', (description) => '*' + description),
					new LodashExtractor('shortName', '')
				),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			new ColumnConfig(
				new ColumnText('ESTIMATED_RISK', 'Estimated Risk (%)', null, { sortable: false }),
				null,
				new StaticValueExtractor(' '), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'estimatedRisk'
			),
			new ColumnConfig(
				new ColumnText('PERFORMANCE_TYPE', 'Performance Type', null, { cellType: 'netPerformanceType' }),
				null,
				new StaticValueExtractor(null), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'netCustomPerformanceType',
				new CombinedStringFormatter('Net', 'Net')
			),
			ColumnConfig.create({
				id: 'oneMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn1m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn3m', desiredValueExpression: 'metricValue' }]
				), labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'yearToDate',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'oneYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn1Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn3Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'fiveYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn5Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'tenYear',
				label: new ColumnText('TEN_YEAR', '10 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn10Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'inception',
				extractor: new ConditionalExtractor(
					performance => performance?.performance?.netExPostMetrics?.[0]?.months < 180,
					new ConditionalExtractor(
						performance => performance?.performance?.netExPostMetrics?.[0]?.months <= 12,
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.netExPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'totalReturn', desiredValueExpression: 'metricValue' }]
						),
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.netExPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'annualizedReturnTotal', desiredValueExpression: 'metricValue' }]
						)),
					new LodashExtractor('', '-')
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			ColumnConfig.create({
				id: 'asOfDate',
				label: new ColumnText('AS_OF_DATE', 'As of'),
				extractor: new LodashExtractor('netExPostMetrics[0].interval.stopDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>)
		],
		FAMILY_PERFORMANCE_COLUMN_CONFIG
	)
);

export const PERFORMANCE_CUSTOM_NET_PDF = new TabConfig(
	'performance.custom.net',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(
			['families', 'familyPerformance'],
			['families', 'familyPerformance'],
			'families.models',
			(portfolio: any, data: any) => {
				return {
					...portfolio,
					...data?.familyPerformance?.models?.find(model => model?.id === portfolio.id) ?? []
				};
			},
			MODEL_PORTFOLIO_FILTER_FUNCTION
		),
		null,
		[
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'modelName',
				label: new ColumnText('MODEL', 'Model'),
				extractor: new StaticValueExtractor(''),
				visible: true
			} as ColumnConfigOptions<StaticValueExtractor>),
			new ColumnConfig(
				new ColumnText('PERFORMANCE_TYPE', 'Performance Type', null, { cellType: 'netPerformanceType' }),
				null,
				new StaticValueExtractor(null), // inexistent data point needed to set static column (current limitation to provide a store extractor)
				'netCustomPerformanceType',
				new CombinedStringFormatter('Net', 'Net')
			),
			ColumnConfig.create({
				id: 'oneMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn1m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeMonth',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturn3m', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'yearToDate',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'oneYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn1Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'threeYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn3Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'fiveYear',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn5Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'tenYear',
				label: new ColumnText('TEN_YEAR', '10 Year (%)', null, { sortable: false }),
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'annualizedReturn10Yr', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'inception',
				extractor: new ConditionalExtractor(
					performance => performance?.performance?.netExPostMetrics?.[0]?.months < 180,
					new ConditionalExtractor(
						performance => performance?.performance?.netExPostMetrics?.[0]?.months <= 12,
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.netExPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'totalReturn', desiredValueExpression: 'metricValue' }]
						),
						new StoreNestedFilterExtractor(
							['familyPerformance'],
							'performance.netExPostMetrics[0].metrics',
							[{ propertyToCompare: 'name', comparedValue: 'annualizedReturnTotal', desiredValueExpression: 'metricValue' }]
						)),
					new LodashExtractor('', '-')
				),
				labelFormatter: NumberFormatter.create({ multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<ConditionalExtractor>),
			ColumnConfig.create({
				id: 'asOfDate',
				label: new ColumnText('AS_OF_DATE', 'As of'),
				extractor: new LodashExtractor('netExPostMetrics[0].interval.stopDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>)
		],
		FAMILY_PERFORMANCE_COLUMN_CONFIG
	)
);
